import { useEffect, useMemo, useState } from "react";
import { message } from "antd";
import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";

import { putLanguageSettings } from "../service";
import { Field, Label, Description } from "V2.0/common/fieldset";
import { Input } from "V2.0/common/input";
import { Switch } from "V2.0/common/switch";
import { Button } from "V2.0/common/button";
import { Select } from "V2.0/common/select";
import { Textarea } from "V2.0/common/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "V2.0/common/table";
import { showErrorToast } from "V2.0/utils";
import { SonioxLanguageNames } from "V2.0/HomePages/Assistants/TranscriptionSettingsWithProviders/models/soniox";

export default LanguageSettings;

// Mirrors backend validation in updateLanguageSettings (SuperdashBackend
// src/routes/api/assistant.ts): non-empty {code,name} pairs, defaultLanguage
// must be one of the codes, customSwitchPolicy capped at 2000 chars.
const MAX_POLICY_LENGTH = 2000;
const MAX_LANGUAGES = 20;
// Mirrors STT_TALLY_DEFAULT_MIN_WORDS / STT_TALLY_MIN_WORDS_RANGE and
// MIN_SWITCH_WORDS_ERROR in the backend tally module (src/libs/common/sttLanguageTally.ts)
const DEFAULT_MIN_SWITCH_WORDS = 5;
const MIN_SWITCH_WORDS = 1;
const MAX_SWITCH_WORDS = 10;
const MIN_SWITCH_WORDS_ERROR =
  "Minimum words to switch must be a whole number from 1 to 10.";

type LanguageEntry = { code: string; name: string };

// On "State of the art" assistants (provider stored as "custom") the codes ARE the
// transcription languages. The table is populated from the stored language hints; only the
// names are editable. The provider's real name is never shown in the UI.
const STATE_OF_THE_ART_PROVIDERS = ["soniox", "custom"]; // stored as "custom" from the UI; never shown to users
const STATE_OF_THE_ART_DEFAULT_HINTS = ["en", "hi"]; // mirrors the backend schema default
const STATE_OF_THE_ART_NAMES: Record<string, string> = SonioxLanguageNames as Record<string, string>;

// "hi-Latn" -> "hi", "en_US" -> "en"
function baseCode(code) {
  return String(code ?? "").trim().toLowerCase().split(/[-_]/)[0];
}

function getHintCodes(transcriptionSettings): string[] {
  const provider = String(transcriptionSettings?.transcriptionProvider ?? "").trim().toLowerCase();
  if (!STATE_OF_THE_ART_PROVIDERS.includes(provider)) return [];
  const stored = transcriptionSettings?.sonioxSettings?.languageHints;
  const hints = Array.isArray(stored) && stored.length > 0 ? stored : STATE_OF_THE_ART_DEFAULT_HINTS;
  return Array.from(new Set(hints.map(baseCode).filter(Boolean)));
}

// Rows = one per hint. A stored entry with the same spoken language keeps its name (the response
// style); a new hint gets the display name from the transcription language list. The default stays if it maps to a hint.
function applyHintsToState(state, hintCodes: string[]) {
  if (hintCodes.length === 0) return state;
  const byBase = new Map<string, LanguageEntry>();
  for (const entry of state.supportedLanguages) {
    const key = baseCode(entry.code);
    if (key && !byBase.has(key)) byBase.set(key, entry);
  }
  const supportedLanguages = hintCodes.map((code) => {
    const existing = byBase.get(code);
    return { code, name: existing?.name?.trim() ? existing.name : (STATE_OF_THE_ART_NAMES[code] ?? code) };
  });
  const currentDefault = baseCode(state.defaultLanguage);
  const defaultLanguage = hintCodes.includes(currentDefault) ? currentDefault : hintCodes[0];
  return { ...state, supportedLanguages, defaultLanguage };
}

function getLanguageSettingsState(languageSettings) {
  const supportedLanguages: LanguageEntry[] =
    languageSettings?.supportedLanguages?.length > 0
      ? languageSettings.supportedLanguages.map(({ code, name }) => ({
          code,
          name,
        }))
      : [{ code: "en", name: "English" }];

  return {
    languageDetectionEnabled:
      languageSettings?.languageDetectionEnabled ?? false,
    supportedLanguages,
    defaultLanguage: languageSettings?.defaultLanguage ?? "en",
    customSwitchPolicy: languageSettings?.customSwitchPolicy ?? "",
    minSwitchWords: String(
      languageSettings?.minSwitchWords ?? DEFAULT_MIN_SWITCH_WORDS,
    ),
  };
}

function LanguageSettings(props) {
  const {
    languageSettings,
    transcriptionSettings,
    workspaceId,
    assistantId,
    syncAssistant,
    locked,
  } = props;

  const hintCodes = getHintCodes(transcriptionSettings);
  const hintDriven = hintCodes.length > 0;
  const hintKey = hintCodes.join(",");

  const [languageState, setLanguageState] = useState(() =>
    applyHintsToState(getLanguageSettingsState(languageSettings), hintCodes),
  );
  const [loading, setLoading] = useState(false);

  // Re-apply when the transcription languages change while this tab is mounted
  useEffect(() => {
    if (!hintDriven) return;
    setLanguageState((prevState) => applyHintsToState(prevState, hintCodes));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hintDriven, hintKey]);

  // Stored entries the hint-driven table cannot keep: shown in a notice, dropped on save
  const droppedEntries: LanguageEntry[] = useMemo(() => {
    if (!hintDriven) return [];
    return (languageSettings?.supportedLanguages ?? []).filter(
      (entry) => !hintCodes.includes(baseCode(entry.code)),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hintDriven, hintKey, languageSettings]);
  const normalisedEntries: LanguageEntry[] = useMemo(() => {
    if (!hintDriven) return [];
    return (languageSettings?.supportedLanguages ?? []).filter(
      (entry) => hintCodes.includes(baseCode(entry.code)) && entry.code !== baseCode(entry.code),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hintDriven, hintKey, languageSettings]);

  const updateLanguageState = (field, value) => {
    setLanguageState((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const updateLanguageEntry = (index, field, value) => {
    setLanguageState((prevState) => {
      const supportedLanguages = prevState.supportedLanguages.map(
        (entry, i) => (i === index ? { ...entry, [field]: value } : entry),
      );
      return { ...prevState, supportedLanguages };
    });
  };

  const addLanguageEntry = () => {
    if (languageState.supportedLanguages.length >= MAX_LANGUAGES) {
      message.error(`A maximum of ${MAX_LANGUAGES} languages is supported`);
      return;
    }
    updateLanguageState("supportedLanguages", [
      ...languageState.supportedLanguages,
      { code: "", name: "" },
    ]);
  };

  const removeLanguageEntry = (index) => {
    updateLanguageState(
      "supportedLanguages",
      languageState.supportedLanguages.filter((_, i) => i !== index),
    );
  };

  const validLanguages = languageState.supportedLanguages.filter(
    (entry) => entry.code.trim() && entry.name.trim(),
  );

  const handleSave = async () => {
    const trimmedLanguages = languageState.supportedLanguages
      .map((entry) => ({ code: entry.code.trim(), name: entry.name.trim() }))
      .filter((entry) => entry.code || entry.name);

    if (trimmedLanguages.length === 0) {
      message.error("Add at least one language");
      return;
    }
    if (trimmedLanguages.some((entry) => !entry.code || !entry.name)) {
      message.error("Every language needs both a code and a name");
      return;
    }

    const codes = trimmedLanguages.map((entry) => entry.code.toLowerCase());
    if (new Set(codes).size !== codes.length) {
      message.error("Language codes must be unique");
      return;
    }

    const defaultLanguage = languageState.defaultLanguage.trim();
    if (!codes.includes(defaultLanguage.toLowerCase())) {
      message.error("Default language must be one of the added languages");
      return;
    }

    if (languageState.customSwitchPolicy.length > MAX_POLICY_LENGTH) {
      message.error(
        `Custom switching rules must be at most ${MAX_POLICY_LENGTH} characters`,
      );
      return;
    }

    // Only State of the art shows the field; Number("") is 0, so an empty input fails here
    const minSwitchWords = Number(String(languageState.minSwitchWords).trim());
    if (
      hintDriven &&
      (!Number.isInteger(minSwitchWords) ||
        minSwitchWords < MIN_SWITCH_WORDS ||
        minSwitchWords > MAX_SWITCH_WORDS)
    ) {
      message.error(MIN_SWITCH_WORDS_ERROR);
      return;
    }

    setLoading(true);
    message.loading("Updating Settings");

    const payload = {
      languageDetectionEnabled: languageState.languageDetectionEnabled,
      supportedLanguages: trimmedLanguages,
      defaultLanguage,
      customSwitchPolicy: languageState.customSwitchPolicy,
      // Omitted on other providers: the backend then keeps the stored value
      ...(hintDriven ? { minSwitchWords } : {}),
    };

    try {
      const response = await putLanguageSettings({
        workspaceId,
        assistantId,
        payload,
      });

      const saved = response.data?.languageSettings ?? payload;

      message.destroy();
      message.success("Settings Updated");
      // Bypassing the hint-driven rows here would let an echoed list that differs from the
      // hints show non-hint rows with disabled code cells until remount.
      setLanguageState(applyHintsToState(getLanguageSettingsState(saved), hintCodes));
      syncAssistant((prevAssistant) => ({
        ...prevAssistant,
        languageSettings: saved,
      }));
    } catch (e) {
      message.destroy();
      showErrorToast(e, "Failed to Update Settings");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-y-4 w-3/4 m-4 min-w-[600px] border rounded-lg">
      <div className="px-6 py-4 bg-gray-50 rounded-t-lg">
        <p className="text-md font-semibold text-gray-900">
          Language Detection
        </p>
        <p className="text-sm mt-1 text-gray-500">
          Detect the language the caller is speaking and respond in it. Also
          applies to inactivity, filler and other canned messages when they
          include per-language variants.
        </p>
        <p className="text-sm mt-1 text-gray-500">
          Language detection works best with the State of the art transcription
          option.
        </p>
      </div>

      <div className="flex flex-col px-6 gap-y-8 py-4">
        <div className="flex items-center gap-x-2">
          <p className="text-sm font-medium">Enable Language Detection</p>
          <Switch
            color="emerald"
            checked={languageState.languageDetectionEnabled}
            onChange={() =>
              updateLanguageState(
                "languageDetectionEnabled",
                !languageState.languageDetectionEnabled,
              )
            }
            disabled={locked}
          />
        </div>

        <div>
          <Field>
            <Label>Supported Languages</Label>
            {hintDriven ? (
              <Description>
                Languages come from your transcription settings (State of the
                art). Edit the name to describe how the assistant should speak
                — for example "Hinglish" or "Spoken Kannada" — it is used
                verbatim in the reply instruction.
              </Description>
            ) : (
              <Description>
                Languages the assistant may detect and switch between. Use short
                codes the way you would write them in message variants, e.g.
                "en", "es", "hi-Latn".
              </Description>
            )}
          </Field>
          <Table dense className="mt-2">
            <TableHead>
              <TableRow>
                <TableHeader>Code</TableHeader>
                <TableHeader>Name</TableHeader>
                <TableHeader className="w-12" />
              </TableRow>
            </TableHead>
            <TableBody>
              {languageState.supportedLanguages.map((entry, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Input
                      value={entry.code}
                      placeholder="en"
                      onChange={(e) =>
                        updateLanguageEntry(index, "code", e.target.value)
                      }
                      // Codes are fixed by the transcription languages on State of the art
                      disabled={locked || hintDriven}
                    />
                  </TableCell>
                  <TableCell>
                    <Input
                      value={entry.name}
                      placeholder="English"
                      onChange={(e) =>
                        updateLanguageEntry(index, "name", e.target.value)
                      }
                      disabled={locked}
                    />
                  </TableCell>
                  <TableCell>
                    {!locked && !hintDriven && languageState.supportedLanguages.length > 1 && (
                      <Button
                        plain
                        onClick={() => removeLanguageEntry(index)}
                      >
                        <TrashIcon className="w-4 h-4" />
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {hintDriven && !locked && droppedEntries.length > 0 && (
            <p className="text-sm text-amber-600 mt-2">
              Not in your transcription languages and removed when you save:{" "}
              {droppedEntries.map((entry) => `${entry.code} (${entry.name})`).join(", ")}
            </p>
          )}
          {hintDriven && normalisedEntries.length > 0 && (
            <p className="text-sm text-gray-500 mt-2">
              Codes are stored as the spoken language:{" "}
              {normalisedEntries.map((entry) => `${entry.code} → ${baseCode(entry.code)}`).join(", ")}
            </p>
          )}
          {!locked && !hintDriven && (
            <Button plain className="mt-2" onClick={addLanguageEntry}>
              <PlusIcon className="w-4 h-4" />
              Add Language
            </Button>
          )}
        </div>

        <div className="w-1/2">
          <Field>
            <Label>Default Language</Label>
            <Description>
              The language the call starts in, and the fallback whenever
              detection is off or unsure
            </Description>
            <Select
              value={languageState.defaultLanguage}
              onChange={(e) =>
                updateLanguageState("defaultLanguage", e.target.value)
              }
              disabled={locked}
            >
              {!validLanguages.some(
                (entry) =>
                  entry.code.trim().toLowerCase() ===
                  languageState.defaultLanguage.trim().toLowerCase(),
              ) && <option value={languageState.defaultLanguage}></option>}
              {validLanguages.map((entry) => (
                <option key={entry.code} value={entry.code.trim()}>
                  {entry.name.trim()} ({entry.code.trim()})
                </option>
              ))}
            </Select>
          </Field>
        </div>

        {hintDriven && (
          <div className="w-1/2">
            <Field>
              <Label>Minimum words to switch language</Label>
              <Description>
                The caller must say at least this many words in another
                language, in one turn, before the assistant switches. A
                complete sentence is usually about 5 words. Explicit requests
                such as "Can we speak English?" always switch.
              </Description>
              <Input
                type="number"
                min={MIN_SWITCH_WORDS}
                max={MAX_SWITCH_WORDS}
                step={1}
                value={languageState.minSwitchWords}
                onChange={(e) =>
                  updateLanguageState("minSwitchWords", e.target.value)
                }
                disabled={locked}
              />
            </Field>
          </div>
        )}

        <div>
          <Field>
            <Label>Custom Switching Rules (Optional)</Label>
            <Description>
              Extra rules for when the assistant should switch languages, in
              plain language. Leave empty to use the built-in behavior: switch
              only on a clear, sustained language change or an explicit request
              from the caller.
            </Description>
            {hintDriven && (
              <Description>
                With State of the art transcription, these rules are only used
                to recognise explicit requests to change language. Use Minimum
                words to switch for everything else.
              </Description>
            )}
            <Textarea
              rows={4}
              maxLength={MAX_POLICY_LENGTH}
              value={languageState.customSwitchPolicy}
              onChange={(e) =>
                updateLanguageState("customSwitchPolicy", e.target.value)
              }
              disabled={locked}
            />
          </Field>
        </div>
      </div>

      {!locked && (
        <div className="bg-gray-50 px-6 py-3 text-right rounded-b-lg">
          <Button onClick={handleSave} disabled={loading}>
            Save
          </Button>
        </div>
      )}
    </div>
  );
}
